import React, { useState, useEffect } from "react";
import { SmallPlainInsetBtn } from "../../buttons";
import {
  commaNumberFormat,
  formatMoneyInput,
} from "../../../utils/commaNumberFormat";
import GeneralDropdown from "../../generalDropDown/generalDropDown";
import "./payContractorsForm.scss";

const STORAGE_KEY = "payContractorsFormState";

export default function PayContractorsForm({
  pendingPayees,
  onClose,
  onSubmit,
}) {
  const [amountFocus, setAmountFocus] = useState(false);

  // 1) Load saved array or init blank ones
  const [entries, setEntries] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
    return pendingPayees.map((p) => ({
      id: p.id,
      userName: p.userName,
      rolePerformed: p.rolePerformed,
      amount: "", // raw string, e.g. "1234.5"
    }));
  });

  // 2) Which payee are we on?
  const [currentIndex, setCurrentIndex] = useState(0);
  // global variable state value applies to all contractors
  const [status, setStatus] = useState("Pending");
  const STATUS_OPTIONS = ["Pending", "Payment Scheduled", "Paid", "Sent"];
  const statusOptions = STATUS_OPTIONS.map((opt) => ({
    value: opt,
    label: opt,
  }));

  // 3) Persist full array whenever it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  }, [entries]);
  const current = entries[currentIndex];
  const showBack = entries.length > 1;
  // 4) Update just the `amount` for this index
  const updateAmount = (raw) => {
    const next = [...entries];
    next[currentIndex] = { ...next[currentIndex], amount: raw };
    setEntries(next);
  };

  const handleBack = () => {
    if (currentIndex > 0) setCurrentIndex((i) => i - 1);
  };

  const handlePay = () => {
    // raw string → parse for log/backend
    const num = parseFloat(current.amount) || 0;
    console.log(`Paying ${current.userName} $${commaNumberFormat(num)}…`);
    // advance or finish
    if (currentIndex < entries.length - 1) {
      // is the last element of a zero-indexed array
      setCurrentIndex((i) => i + 1); // go to the next if it has not reached the last one yet
    } else {
      // FINAL step: hand parent the whole batch
      localStorage.removeItem(STORAGE_KEY);
      onClose();
      onSubmit(entries, status);
    }
  };

  return (
    <div className="pay-contractors-form-backdrop">
      <div className="pay-contractors-form">
        <h2>Pay Contractors</h2>
        <div className="field">
          <label>Status</label>
          <GeneralDropdown
            options={statusOptions}
            value={status}
            onChange={setStatus}
            placeholder="Select status"
            className="status-dropdown"
          />
        </div>
        <p>
          <strong>Payee:</strong> {current.userName} ({current.rolePerformed})
        </p>
        <div className="field">
          <label>Amount</label>
          <div className="amount-input-wrapper blinker-wrapper">
            <span className="currency-sign">$</span>

            <input
              type="text"
              value={formatMoneyInput(current.amount)}
              onChange={(e) => {
                const raw = e.target.value
                  .replace(/,/g, "")
                  .replace(/[^0-9.]/g, "");
                updateAmount(raw);
              }}
              placeholder="0"
              onFocus={() => setAmountFocus(true)}
              onBlur={() => setAmountFocus(false)}
            />
            {!amountFocus && !current.amount && (
              <span className="blinking-cursor-gold-color amount-cursor">
                |
              </span>
            )}
          </div>
        </div>

        <div className="actions">
          {showBack && (
            <SmallPlainInsetBtn
              text="Back"
              onClick={handleBack}
              disabled={currentIndex === 0}
              width="100px"
            />
          )}

          <SmallPlainInsetBtn
            text={
              currentIndex < entries.length - 1 ? "Pay & Next" : "Pay & Done"
            }
            onClick={handlePay}
            disabled={!current.amount}
            width="120px"
          />

          <SmallPlainInsetBtn
            text="Cancel"
            onClick={() => {
              localStorage.removeItem(STORAGE_KEY);
              onClose();
            }}
            width="100px"
          />
        </div>
      </div>
    </div>
  );
}
